import { RefObject, useContext, useEffect } from 'react';
import { TableCursorScrollContext } from '../context/TableCursorScrollContext';

/** Порог в px: зажатие+движение мыши считается протяжкой, а не кликом, только после него. */
const DRAG_THRESHOLD = 5;

interface IUseDragScrollOptions {
    /** Селектор элементов, чьи собственные drag-жесты имеют приоритет над прокруткой. */
    ignoreSelector?: string;
    /** Значения, после изменения которых scroll-контейнер мог перемонтироваться. */
    recalcOn?: unknown[];
}

/**
 * Drag-to-scroll («Прокрутка курсором в таблицах») для нативного scroll-контейнера.
 *
 * При включённой глобальной опции пользователь может «схватить» содержимое мышью
 * и тащить — контейнер скроллится по обеим осям. Только мышь перехватывается:
 * touch/pen продолжают использовать нативный скролл.
 */
export function useDragScroll(
    ref: RefObject<HTMLElement | null>,
    { ignoreSelector, recalcOn = [] }: IUseDragScrollOptions = {},
): void {
    const enabled = useContext(TableCursorScrollContext);

    useEffect(() => {
        const el = ref.current;
        if (!el || !enabled) return;

        el.classList.add('is-grab');

        let down = false;
        let dragging = false;
        let suppressClick = false;
        let startX = 0;
        let startY = 0;
        let startLeft = 0;
        let startTop = 0;

        const onMouseMove = (e: MouseEvent) => {
            if (!down) return;
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            if (!dragging) {
                if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
                dragging = true;
                el.classList.add('is-dragging');
            }
            el.scrollLeft = startLeft - dx;
            el.scrollTop = startTop - dy;
            e.preventDefault();
        };

        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp, true);
            if (!down) return;
            down = false;
            if (dragging) {
                dragging = false;
                el.classList.remove('is-dragging');
                suppressClick = true;
            }
        };

        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) return;
            suppressClick = false;
            const target = e.target instanceof Element ? e.target : null;
            if (target?.closest('.is-grab') !== el) return;
            if (ignoreSelector && target?.closest(ignoreSelector)) return;

            const rect = el.getBoundingClientRect();
            if (
                e.clientX >= rect.left + el.clientLeft + el.clientWidth ||
                e.clientY >= rect.top + el.clientTop + el.clientHeight
            ) {
                return;
            }
            down = true;
            dragging = false;
            startX = e.clientX;
            startY = e.clientY;
            startLeft = el.scrollLeft;
            startTop = el.scrollTop;
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp, true);
        };

        const onClickCapture = (e: MouseEvent) => {
            if (!suppressClick) return;
            suppressClick = false;
            e.stopPropagation();
            e.preventDefault();
        };

        el.addEventListener('mousedown', onMouseDown);
        el.addEventListener('click', onClickCapture, true);

        return () => {
            el.classList.remove('is-grab', 'is-dragging');
            el.removeEventListener('mousedown', onMouseDown);
            el.removeEventListener('click', onClickCapture, true);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp, true);
        };
        // recalcOn — внешний триггер переподключения, когда контейнер появляется после загрузки данных.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, enabled, ignoreSelector, ...recalcOn]);
}
